import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";
import { useLive } from "../live";
import type { OpDay, OpList, QueueCat, ReminderView, SessionUser } from "../types";
import { useMessage } from "./Message";
import OpDayPicker from "./OpDayPicker";
import { fundingLabel } from "./OpDayDialog";
import { catHasRoom, isOperatingList, listTitle, moveNeed, noRoomText } from "./op-move";
import ReminderEditor from "./ReminderEditor";
import { todayIso } from "../docs/helpers";
import { BellIcon, cardWaitText, dayShort, defaultDayList, dueText, reminderWhen, ruDate } from "./reminders";

/** Как часто продлевать отметку «открыта у …», пока карточка открыта (сервер держит 10 минут). */
const HOLD_EVERY_MS = 3 * 60_000;

/**
 * Снятие отметки — с задержкой и отменой: карточку, закрытую и тут же открытую
 * снова (или смонтированную дважды в режиме разработки), запоздавшее снятие
 * оставило бы без отметки — запросы идут параллельно, и DELETE мог прийти
 * после нового POST.
 */
const pendingRelease = new Map<number, number>();

/** «2026-11-12 14:05:00» → «12.11.2026 14:05». */
function stamp(iso: string | null): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}:\d{2})/.exec(iso ?? "");
  return m ? `${m[3]}.${m[2]}.${m[1]} ${m[4]}` : "";
}

/** Первый номер из «8-900-111-22-33, 8-901-…» — для ссылки «позвонить». */
function telHref(phone: string): string {
  const first = phone.split(/[,;/]|\s{2,}/)[0] ?? "";
  return `tel:${first.replace(/[^\d+]/g, "")}`;
}

/**
 * Карточка напоминания: кто, телефон, что за операция и оплата, комментарий —
 * и что с этим сделать.
 *
 * «Записать на операцию» — календарь опердней с нужного месяца: из резерва,
 * очереди и отказников пациент переносится в оперирующий список на выбранный
 * день, из листа ожидания — назначается на день, в оперирующем — переносится на
 * другой день. После этого напоминание выполнено — у всех. «В отказники» и «в
 * другой список» — то же, что в меню строки, и тоже закрывают напоминание.
 *
 * Пока карточка открыта, на напоминании стоит отметка «открыта у …»: коллега
 * видит её в колокольчике и в своей карточке и не звонит тому же пациенту.
 * Каждое действие сначала проверяет, что напоминание ещё открыто, — если его
 * уже выполнили, сервер говорит, кто и когда.
 */
export default function ReminderCard({
  remId,
  user,
  onClose,
  onOpenPatient,
}: {
  remId: number;
  user: SessionUser;
  onClose: () => void;
  /** «Карточка пациента» — раздел «Пациенты»; без него — новая вкладка. */
  onOpenPatient?: (patId: number) => void;
}) {
  const message = useMessage();
  const [view, setView] = useState<ReminderView | null>(null);
  const [lists, setLists] = useState<OpList[]>([]);
  const [mode, setMode] = useState<"main" | "edit" | "lists" | "cats">("main");
  const [heldBy, setHeldBy] = useState<{ fio: string; at: string } | null>(null);
  const [busy, setBusy] = useState(false);
  /** Календарь опердней: записать (book) или перенести в оперирующий список (move). */
  const [picker, setPicker] = useState<{
    anchor: DOMRect;
    days: OpDay[];
    list: OpList;
    purpose: "book" | "move";
  } | null>(null);
  const [moveTarget, setMoveTarget] = useState<OpList | null>(null);
  const [cats, setCats] = useState<QueueCat[] | null>(null);
  /** Щелчок мимо календаря только закрывает календарь, а не всю карточку. */
  const pickerJustClosed = useRef(false);

  const load = useCallback(async () => {
    try {
      const r = await api.reminder(remId);
      setView(r.reminder);
    } catch (err) {
      message(err as Error);
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remId]);

  useEffect(() => {
    load();
    api
      .opLists()
      .then((r) => setLists(r.lists))
      .catch(() => {});
  }, [load]);

  // «Открыта у меня» — пока карточка открыта; закрыли — отметка снята.
  useEffect(() => {
    let alive = true;
    const pending = pendingRelease.get(remId);
    if (pending) {
      window.clearTimeout(pending);
      pendingRelease.delete(remId);
    }
    const hold = () =>
      api
        .holdReminder(remId)
        .then((r) => alive && setHeldBy(r.held && r.held.user_id !== user.user_id ? r.held : null))
        .catch(() => {});
    hold();
    const timer = window.setInterval(hold, HOLD_EVERY_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
      pendingRelease.set(
        remId,
        window.setTimeout(() => {
          pendingRelease.delete(remId);
          api.releaseReminder(remId).catch(() => {});
        }, 400)
      );
    };
  }, [remId, user.user_id]);

  // Коллега выполнил или изменил напоминание, открыли день — карточка видит сразу.
  useLive(["reminders", "op-lists"], load, { paused: busy || !!picker || mode === "edit" });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !picker && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [picker, busy, onClose]);

  // Календарь — в портале поверх карточки; щелчок мимо него закрывает только его.
  useEffect(() => {
    if (!picker) return;
    const onDown = (ev: MouseEvent) => {
      if ((ev.target as HTMLElement).closest?.(".opw-cal")) return;
      setPicker(null);
      pickerJustClosed.current = true;
      window.setTimeout(() => (pickerJustClosed.current = false), 400);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [picker]);

  const closeByOverlay = () => {
    if (busy || picker || pickerJustClosed.current) return;
    onClose();
  };

  // Порталом в body: шапка, откуда открывают карточку, может быть «стеклом»
  // (backdrop-filter), а внутри него position: fixed считается от шапки.
  if (!view) {
    return createPortal(
      <div className="iol-dlg-overlay overlay" onClick={onClose}>
        <div className="iol-dlg rem-card rem-card-loading" onClick={(e) => e.stopPropagation()} />
      </div>,
      document.body
    );
  }

  const e = view.entry;
  const open = view.status === "open";
  const listLabel = e.list_section && e.list_section !== e.list_name ? `${e.list_section} · ${e.list_name}` : e.list_name;
  const refused = lists.find((l) => l.code === "refused") ?? null;

  /** Напоминание ещё открыто? Заодно — отметка «открыта у меня». */
  async function claim(): Promise<boolean> {
    try {
      const r = await api.holdReminder(remId);
      setHeldBy(r.held && r.held.user_id !== user.user_id ? r.held : null);
      return true;
    } catch (err) {
      message(err as Error);
      await load();
      return false;
    }
  }

  /** Строку уже записали или перенесли — закрыть напоминание у всех. */
  async function finish(note: string, text: string) {
    try {
      await api.reminderDone(remId, note);
      message(`${text} — напоминание выполнено`);
    } catch (err) {
      // Сделанное уже сделано; напоминание успел закрыть кто-то другой.
      message(`${text}. ${(err as Error).message}`, "error");
    }
    onClose();
  }

  async function openBooking(anchorEl: HTMLElement) {
    const list =
      (view!.day_list_id ? lists.find((l) => l.list_id === view!.day_list_id) : null) ??
      defaultDayList(lists, e);
    if (!list) {
      message("Нет списка с операционными днями", "error");
      return;
    }
    try {
      const r = await api.opDays(list.list_id);
      setPicker({ anchor: anchorEl.getBoundingClientRect(), days: r.days, list, purpose: "book" });
    } catch (err) {
      message(err as Error);
    }
  }

  async function book(day: OpDay) {
    setPicker(null);
    setBusy(true);
    try {
      if (!(await claim())) return;
      if (e.list_kind === "waiting") await api.planOpEntry(view!.entry_id, { plan_day_id: day.day_id });
      else if (e.list_id === day.list_id) await api.rescheduleOpEntry(view!.entry_id, day.day_id);
      else await api.moveOpEntry(view!.entry_id, day.list_id, { day_id: day.day_id });
      await finish(`записан(а) на ${day.label}`, `«${e.pat_fio}» записан(а) на ${day.label}`);
    } catch (err) {
      message(err as Error);
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function markDone() {
    setBusy(true);
    try {
      await api.reminderDone(remId);
      message(`«${e.pat_fio}» — напоминание выполнено`);
      onClose();
    } catch (err) {
      message(err as Error);
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function moveTo(target: OpList, to: { day_id?: number; queue_cat_id?: number } = {}, where = "") {
    setPicker(null);
    setBusy(true);
    try {
      if (!(await claim())) return;
      await api.moveOpEntry(view!.entry_id, target.list_id, to);
      const text = `«${e.pat_fio}» перенесён(а) в «${listTitle(target)}»${where}`;
      await finish(`перенесён(а) в «${listTitle(target)}»${where}`, text);
    } catch (err) {
      message(err as Error);
      await load();
    } finally {
      setBusy(false);
    }
  }

  /** Выбрали список: в оперирующий — день, в очередь — категория, в остальные — сразу. */
  async function chooseList(target: OpList, el: HTMLElement) {
    if (!isOperatingList(target) && !target.queue_cats) {
      await moveTo(target);
      return;
    }
    setMoveTarget(target);
    try {
      const need = await moveNeed(target);
      if (need.kind === "day") {
        setPicker({ anchor: el.getBoundingClientRect(), days: need.days, list: target, purpose: "move" });
      } else if (need.kind === "cat") {
        if (!need.cats.length) {
          message(noRoomText(target, need.cats), "error");
          return;
        }
        setCats(need.cats);
        setMode("cats");
      }
    } catch (err) {
      message(err as Error);
    }
  }

  const want = view.kind === "month" && view.want_from && view.want_to ? { from: view.want_from, to: view.want_to } : null;

  return createPortal(
    <div className="iol-dlg-overlay overlay rem-card-overlay" onClick={closeByOverlay}>
      <div className="iol-dlg rem-card" onClick={(ev) => ev.stopPropagation()}>
        <div className="rem-card-head">
          <BellIcon size={17} />
          <span className="rem-card-title">Напоминание</span>
          <span className={`rem-state rem-state-${!open ? "done" : view.due ? "due" : "wait"}`}>
            {!open ? "выполнено" : view.due ? "пора" : "ждёт"}
          </span>
          <i className="rem-card-x" title="Закрыть" onClick={busy ? undefined : onClose}>
            ×
          </i>
        </div>

        <div className="rem-card-fio">
          {e.pat_fio}
          {e.pat_bdate && <span className="rem-card-bdate">{ruDate(e.pat_bdate)} г. р.</span>}
        </div>
        <div className="rem-card-contact">
          {e.pat_phone ? (
            <a className="rem-card-phone" href={telHref(e.pat_phone)}>
              {e.pat_phone}
            </a>
          ) : (
            <span className="rem-card-phone rem-card-nophone">телефон не записан</span>
          )}
          {!!e.pat_id && (
            <u
              className="hist-link rem-card-pat"
              onClick={() => {
                if (onOpenPatient) {
                  onClose();
                  onOpenPatient(e.pat_id);
                } else window.open(`/?pat=${e.pat_id}`, "_blank");
              }}
            >
              карточка пациента
            </u>
          )}
        </div>

        <div className="rem-card-facts">
          <span>Список</span>
          <b>
            {listLabel}
            {e.queue_cat_name ? ` · ${e.queue_cat_name}` : ""}
          </b>
          <span>Операция</span>
          <b>{[e.oper_type, e.eye].filter(Boolean).join(", ") || "—"}</b>
          <span>Оплата</span>
          <b>
            {e.funding ? (
              <em className={`op-fund-chip op-fund-${e.funding}`}>{fundingLabel(e.funding)}</em>
            ) : (
              "не указана"
            )}
          </b>
          {e.surgeon && (
            <>
              <span>Хирург</span>
              <b>{e.surgeon}</b>
            </>
          )}
          {view.booked && (
            <>
              <span>Сейчас</span>
              <b>записан(а) на {view.booked.label}</b>
            </>
          )}
          {e.comment && (
            <>
              <span>Комментарий</span>
              <b className="rem-card-comment">{e.comment}</b>
            </>
          )}
        </div>

        <div className={`rem-card-when${open && view.due ? " due" : ""}`}>
          <div className="rem-card-when-main">
            {view.kind === "month" ? (
              <>
                Хочет оперироваться: <b>{reminderWhen(view)}</b>
                {view.day_list_name && <span className="rem-card-list"> · дни «{view.day_list_name}»</span>}
              </>
            ) : (
              <>
                Напомнить: <b>{ruDate(view.remind_on)}</b>
              </>
            )}
          </div>
          {view.note && <div className="rem-card-note">{view.note}</div>}
          {open && <div className="rem-card-why">{view.due ? dueText(view) : cardWaitText(view, todayIso())}</div>}
          {open && view.due_days.length > 0 && (
            <div className="rem-card-days">
              {view.due_days.map((d) => (
                <span key={d.day_id} title={d.label}>
                  {dayShort(d.label)}
                  <i>
                    {d.taken}
                    {d.planned > 0 ? ` из ${d.planned}` : ""}
                  </i>
                </span>
              ))}
            </div>
          )}
          <div className="rem-card-by">
            поставил(а) {view.created_fio ?? "—"}, {ruDate(view.created_at)}
            {view.updated_fio && view.updated_at ? ` · изменил(а) ${view.updated_fio}, ${ruDate(view.updated_at)}` : ""}
          </div>
        </div>

        {open && heldBy && (
          <div className="rem-card-held">
            Карточку сейчас открыл(а) {heldBy.fio} — договоритесь, кто звонит.
          </div>
        )}
        {!open && (
          <div className="rem-card-done">
            Выполнено{view.done_fio ? `: ${view.done_fio}` : ""}
            {view.done_at ? `, ${stamp(view.done_at)}` : ""}
            {view.done_note ? ` — ${view.done_note}` : ""}
          </div>
        )}

        {mode === "edit" && open && (
          <ReminderEditor
            inline
            entry={{ entry_id: view.entry_id, pat_fio: e.pat_fio, list_id: e.list_id, oper_type: e.oper_type }}
            reminder={view}
            onSaved={(r) => {
              setView(r);
              setMode("main");
            }}
            onCancel={() => setMode("main")}
            onDeleted={onClose}
          />
        )}

        {mode === "lists" && open && (
          <div className="rem-card-pick">
            <div className="rem-card-pick-head">
              В какой список
              <u className="hist-link" onClick={() => setMode("main")}>
                назад
              </u>
            </div>
            {lists
              .filter((l) => l.list_id !== e.list_id)
              .map((l) => (
                <div
                  key={l.list_id}
                  className={`rem-card-pick-item${busy ? " disabled" : ""}`}
                  onClick={(ev) => !busy && chooseList(l, ev.currentTarget)}
                >
                  {listTitle(l)}
                  {(isOperatingList(l) || l.queue_cats) && <i>›</i>}
                </div>
              ))}
          </div>
        )}

        {mode === "cats" && open && moveTarget && (
          <div className="rem-card-pick">
            <div className="rem-card-pick-head">
              Категория — «{moveTarget.name}»
              <u className="hist-link" onClick={() => setMode("lists")}>
                назад
              </u>
            </div>
            {(cats ?? []).map((c) => {
              const full = !catHasRoom(c);
              return (
                <div
                  key={c.cat_id}
                  className={`rem-card-pick-item${full || busy ? " disabled" : ""}`}
                  title={full ? "Мест нет — категория заполнена" : `Свободно мест: ${c.slots - c.taken}`}
                  onClick={
                    full || busy
                      ? undefined
                      : () => moveTo(moveTarget, { queue_cat_id: c.cat_id }, `, категория «${c.name}»`)
                  }
                >
                  {c.name}
                  <i>
                    {c.taken} из {c.slots}
                  </i>
                </div>
              );
            })}
          </div>
        )}

        {mode === "main" && (
          <div className="rem-card-actions">
            {open && (
              <>
                <div
                  className={`button green rem-book${busy ? " disabled" : ""}`}
                  onClick={(ev) => !busy && openBooking(ev.currentTarget)}
                >
                  Записать на операцию
                </div>
                <div className={`button rem-done${busy ? " disabled" : ""}`} onClick={busy ? undefined : markDone}>
                  Выполнено ✓
                </div>
                <div className="button rem-edit" onClick={() => setMode("edit")}>
                  Изменить напоминание
                </div>
                {refused && e.list_id !== refused.list_id && (
                  <div
                    className={`button rem-refuse${busy ? " disabled" : ""}`}
                    onClick={busy ? undefined : () => moveTo(refused)}
                  >
                    В отказники
                  </div>
                )}
                <div className="button rem-move" onClick={() => setMode("lists")}>
                  В другой список…
                </div>
              </>
            )}
            <div className="button rem-close" onClick={busy ? undefined : onClose}>
              Закрыть
            </div>
          </div>
        )}

        {picker && (
          <OpDayPicker
            anchor={picker.anchor}
            current={picker.purpose === "book" ? (e.list_kind === "waiting" ? e.plan_day_id : e.day_id) : null}
            surgeon={e.surgeon}
            days={picker.days}
            startMonth={want?.from ?? picker.days[0]?.op_date ?? null}
            want={want}
            className="opw-cal-over"
            onPick={(day) =>
              picker.purpose === "book" ? book(day) : moveTo(picker.list, { day_id: day.day_id }, ` на ${day.label}`)
            }
            onClose={() => setPicker(null)}
          />
        )}
      </div>
    </div>,
    document.body
  );
}
